import React, { useEffect, useRef, useState } from 'react';
import { LiturgicalYear, YearChurch } from './types';
import './YearChurches.css';

/**
 * The churches in the year on screen.
 *
 * One church: its name, as a label. Two or more: a toggle per church, all on
 * unless you turn one off. Turning off the last one is not possible; the baseline
 * is everyone in this year, not an empty page. A narrowing lasts only while you
 * stay in the year; moving years starts again from everyone.
 *
 * In the current year the toggles start from your saved default (Jack: St Luke's
 * alone). When you change them, "Make default" saves the new set.
 */
export const ChurchToggles: React.FC<{
  churches: YearChurch[];
  hidden: number[];
  onChange: (hidden: number[]) => void;
  isCurrentYear: boolean;
  defaultDiffers: boolean;
  onMakeDefault: () => void;
}> = ({ churches, hidden, onChange, isCurrentYear, defaultDiffers, onMakeDefault }) => {
  if (churches.length === 0) return null;
  if (churches.length === 1) {
    return <span className="year-church-label">{churches[0].church_name}</span>;
  }
  const toggle = (id: number) => {
    const isOff = hidden.includes(id);
    if (isOff) {
      onChange(hidden.filter(h => h !== id));
    } else if (hidden.length < churches.length - 1) {
      onChange([...hidden, id]);
    }
  };
  return (
    <div className="year-church-toggles" role="group" aria-label="Churches in this year">
      {churches.map(c => {
        const on = !hidden.includes(c.church_id);
        return (
          <button
            key={c.church_id}
            type="button"
            className={`year-church-toggle${on ? ' on' : ''}`}
            aria-pressed={on}
            onClick={() => toggle(c.church_id)}
            title={`${c.services} services`}
          >
            {c.church_name}
          </button>
        );
      })}
      {isCurrentYear && defaultDiffers && (
        <button type="button" className="year-church-default" onClick={onMakeDefault}>
          Make default
        </button>
      )}
    </div>
  );
};

/**
 * The year menu: every liturgical year, newest first, with the churches that
 * have services in it, so the list reads as a timeline of where you were.
 */
export const YearMenu: React.FC<{
  years: LiturgicalYear[];
  current: LiturgicalYear;
  yearChurches: YearChurch[];
  onPick: (yearId: number) => void;
}> = ({ years, current, yearChurches, onPick }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const churchesOf = (yearId: number) => yearChurches.filter(c => c.year_id === yearId);
  const [main, sub] = splitYear(current.year);

  return (
    <div className="year-menu" ref={ref}>
      <button
        type="button"
        className="year-display year-menu-button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Choose a year"
      >
        <div className="year-main">{main}</div>
        <div className="year-sub">{sub}</div>
      </button>
      {open && (
        <ul className="year-menu-list" role="listbox">
          {years.map(y => {
            const cs = churchesOf(y.id);
            const [m, s] = splitYear(y.year);
            return (
              <li
                key={y.id}
                role="option"
                aria-selected={y.id === current.id}
                className={`year-menu-item${y.id === current.id ? ' current' : ''}${cs.length === 0 ? ' empty' : ''}`}
                onClick={() => { setOpen(false); onPick(y.id); }}
              >
                <span className="year-menu-year">{m} <small>{s}</small></span>
                <span className="year-menu-churches">
                  {cs.length === 0 ? 'no services' : cs.map(c => c.church_name).join(', ')}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/** "Year B 2008-2009" -> ["2008-2009", "Year B"]; names with an underscore split the same way. */
export function splitYear(label: string): [string, string] {
  const m = label.match(/^(Year [ABC])[ _](\d{4}-\d{4})$/);
  return m ? [m[2], m[1]] : [label, ''];
}
